import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validator, Validators, FormControl } from '@angular/forms';
import { PointStoreProductEditService } from '../../marketplace/admin/services/point-store-product-edit.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { Subject } from 'rxjs';

const validationDateMessageType = {
    product_info: 'product_info',
    settings: 'settings'
  };

const forbiddenCharacterType = {
  title: 'title',
  description: 'description',
  select: 'select'
};
  
type ValidationDateMessageType = keyof typeof validationDateMessageType;
type ForbiddenCharacterType = keyof typeof forbiddenCharacterType;


@Injectable({
  providedIn: 'root'
})
export class ValidatorService {

  onlyLetterRegex: RegExp = /^[a-zA-Z\s]*$/;
  digitsPattern: RegExp = /^\d+(,\d{3})*$/;
  forbiddenTitleCharacters: string[] = ['\\', '/', '!', '+', '<', '>', '?', '#', '&', ',', '%'];
  forbiddenDescriptionCharacters: string[] = ['\\', '/', '+', '<', '>', '#', '&', '%'];
  validationDateMessageType = {
    product_info: 'product_info',
    settings: 'settings'
  }
  private variationSubject = new Subject<any>();
  variationObservable$ = this.variationSubject.asObservable();

  constructor(
    private pointStoreProductEditService: PointStoreProductEditService,
    public cvLocalizationService: CVLocalizationService
  ) { }

  /**
   * validate the control up to five digits
   * @param control formcontrol
   * @returns ValidationErrors | null
   */
  upToFiveDigits(control: FormControl): ValidationErrors | null {
    const value = control.value?.toString().replace(/,/g, '');
    if (!/^\d{1,5}(,\d{3})*$/.test(value)) {
      return { fiveDigits: true }
    }
    return null;
  }

  /**
   * validate the control up to seven digits
   * @param control FormControl
   * @returns ValidationErrors | null
   */
  upToSevenDigits(control: FormControl): ValidationErrors | null {
    const value = control.value?.toString().replace(/,/g, '');
    if (!/^\d{1,7}(,\d{3})*$/.test(value)) {
      return { sevenDigits: true }
    }
    return null;
  }

  /**
   * validate the control to allow only numbers
   * @param control FormControl
   * @returns ValidationErrors | null
   */
  onlyDigits(control: FormControl): ValidationErrors | null {
    const value = control.value;
    const parsedValue = Number(value);
    if (!isNaN(parsedValue)) {
      return null;
    }
    return { notNumeric: true };
  }

  /**
   * validate the control in case the variation name is repeated
   * @returns ValidatorFn
   */
  distinctVariationName(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const variationName = control.value.toLowerCase();
      const productList: any[] = this.pointStoreProductEditService.pointStoreProductEditObj.collection.originalData;
      const hasDistincVariationName = productList?.some(products => (products.VariationsData || []).some(variation => variation.VariationName?.toLowerCase() === variationName));
      return hasDistincVariationName ? { distinct: true } : null;
    }
  }

  requiredTrimValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isWhitespace = (control.value || '').trim().length === 0;
      const isValid = !isWhitespace;  
      return isValid ? null : { required: true };
    };
  }

  forbiddenCharactersValidator(forbiddenChars: string[], type: ForbiddenCharacterType): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const forbiddenRegex = new RegExp(`[${forbiddenChars.join('\\')}]+`, 'g');
      let error = null;
      let value = null;
      switch (type) {
        case 'title':
          value = control.value;
          if (forbiddenRegex.test(value)) {
            error = { forbiddenCharacters: true };
          }
          break;
        case 'description':
          value = control.value;
          if (forbiddenRegex.test(value)) {
            error = { forbiddenCharactersDescription: true };
          }
          break;
        case 'select':
          value = control.value?.name;
          if (forbiddenRegex.test(value)) {
            error = { forbiddenCharacters: true };
          }
          break;
        default:
          break;
      }
      return error;
    }
  }

  /**
   * 
   * @param control AbstractControl
   * @returns boolean
   */
  displayError(control: AbstractControl): boolean {
    return control && control.errors && (control.touched || control.dirty);
  }

  /**
   * 
   * @param control AbstractControl
   * @returns string
   */

  getErrorMessage(control: AbstractControl): string {
    let errorMessage = '';
    if (!control) {
      return errorMessage;
    }
    let maxLengthErrMsg = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Up_To', 'Up to {0} digits');
    if (control.hasError('required')) {
      const formName = this.getControlName(control);
      if (formName) {
        let customErrorMessage = this.getCustomErrorMessages(formName);
        if (customErrorMessage != "") { errorMessage = customErrorMessage; }
        else {
          errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Field_Required', 'This field is required');
        }
      } else {
        errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Field_Required', 'This field is required');
      }
    } else if (control.hasError('maxlength')) {
      const error = control.errors?.maxlength;
      errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Entry_Long', 'This entry is too long');
    } else if (control.hasError('min')) {
      const error = control.errors?.min;
      let minValueErrMsg = `${this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Min_Value', 'Min value')} {0}`;
      errorMessage = minValueErrMsg.replace('{0}', error.min);
    } else if (control.hasError('max')) {
      const error = control.errors?.max;
      let maxValueErrMsg = `${this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Max_Value', 'Max value')} {0}`;
      errorMessage = maxValueErrMsg.replace('{0}', error.max);
    } else if (control.hasError('pattern')) {
      const error = control.errors?.pattern;
      if (error.requiredPattern === String(this.digitsPattern)) {
        errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Only_Digits', 'Only digits');
      } else if (error.requiredPattern === String(this.onlyLetterRegex)) {
        errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Only_Letters', 'Only letters');
      }
    } else if (control.hasError('fiveDigits')) {
      errorMessage = maxLengthErrMsg.replace('{0}', '5');
    } else if (control.hasError('distinct')) {
      errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Name_Already_Exists', 'This name already exists');
    } else if (control.hasError('sevenDigits')) {
      errorMessage = maxLengthErrMsg.replace('{0}', '7');
    } else if (control.hasError('notNumeric')) {
      errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Only_Digits', 'Only digits');
    } else if (control.hasError('forbiddenCharacters')) {
      let forbiddenMsg = `${this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Cannot_Use', 'Cannot use')} {0}`;      
      errorMessage = forbiddenMsg.replace('{0}', this.forbiddenTitleCharacters.join(' '));
    } else if (control.hasError('forbiddenCharactersDescription')) {
      let forbiddenMsg = `${this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Cannot_Use', 'Cannot use')} {0}`;
      errorMessage = forbiddenMsg.replace('{0}', this.forbiddenDescriptionCharacters.join(' '));
    } else if (control.hasError('titleExists')) {
      errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Product_Title_Exist', 'Product title already exists');
    } else if (control.hasError('dateRangeInfo')) {
      errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Date_Range', 'The Until date must be greater than or equal to the From date');
    } else if (control.hasError('dateRangeSettings')) {
      errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Date_Range_Settings', 'The End date must be greater than or equal to the Start date');
    }
    return errorMessage;
  }

  /**
   * 
   * @param control AbstractControl
   * @returns string | null
   */

  getControlName(control: AbstractControl): string | null {
    const formGroup = control.parent.controls;
    return Object.keys(formGroup).find(name => control === formGroup[name]) || null;
  }

  /**
   * 
   * @param key string
   * @returns string
   */
  
  getCustomErrorMessages(key: string): string {
    let errorMessage: string = '';
    switch (key) {
      case "carouselStart":
        errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Validate_StartDate_Carousel', 'Please select a start date for the Carousel display setting.');
        break;
      case "featureStart":
        errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Start_Date_Feature_Validation', 'Please select a start date for the Feature display setting.');
        break;
      case "saleStart":
        errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Start_Date_Discount_Validation', 'Please select a start date for the Discount display setting.');
        break;
      case "percentageInput":
        errorMessage = this.cvLocalizationService.getCachedText('MarketPlace_Management_Messages', 'Field_Required', 'This field is required');
        break;
      default:
        break;
    }
    return errorMessage;
  }

  preventLeadingZero(event: any, control: AbstractControl) {
    setTimeout(() => {
      let inputField = event.target;
      let value = inputField.value;
      if (value && value.charAt(0) === '0') {
        let newValue = value.replace(/^0+/, '');
        control.setValue(newValue);
      }
    }, 0);
  }

  /**
 * 
 * @param date date to truncate
 * @param start start or end date. If true start date else end date
 * @returns truncated date
 */
  truncateDate(date: Date, start: boolean = false) {
    if (!date) return date;
    if (start) {
      date.setHours(0, 0, 0, 0);
    } else {
      date.setHours(12, 0, 0, 0);
    }
    return new Date(date.toISOString());
  }

  dateRangeValidator(type: ValidationDateMessageType): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        // return control.value ? { dateRange: true } : null;
        if (control.value) {
            switch (type) {
                case 'product_info':
                    return { dateRangeInfo: true };
                case 'settings':
                    return { dateRangeSettings: true };
                default:
                    return null;
            }
        } else {
            return null;
        }
    }
  }

  notifiedVariationChange(data: any) {
    this.variationSubject.next(data);
  }
}
